import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, Loader2, X, Trash2, ShieldCheck, Pencil, Eye, User as UserIcon, LockKeyhole, Unlock } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { listUsers, createUser, updateUser, deleteUser, listLockedAccounts, unlockUser } from '../lib/marketingApi';
import { formatApiError } from '../lib/auth';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access incl. user management', icon: ShieldCheck, cls: 'bg-rose-100 text-rose-700' },
  { value: 'editor', label: 'Editor', desc: 'Edit enquiries, statuses & notes', icon: Pencil, cls: 'bg-amber-100 text-amber-700' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access', icon: Eye, cls: 'bg-slate-100 text-slate-600' },
];
const roleMeta = (v) => ROLES.find((r) => r.value === v) || ROLES[2];

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch (e) { return iso; }
}
function fmtDT(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}
const emptyForm = { email: '', name: '', password: '', role: 'viewer' };

export default function UserManagement() {
  const { user: me } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editPw, setEditPw] = useState('');
  const [locked, setLocked] = useState([]);

  const loadLocked = useCallback(async () => {
    try { const r = await listLockedAccounts(); setLocked(r.items || []); } catch (e) { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listUsers();
      setUsers(res.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadLocked(); }, [load, loadLocked]);

  const onUnlock = async (row) => {
    try {
      await unlockUser(row.user_id);
      toast.success(`${row.email} unlocked`);
      setLocked((prev) => prev.filter((x) => x.user_id !== row.user_id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Unlock failed'));
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createUser(form);
      toast.success('User created');
      setShowCreate(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Could not create user'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      const res = await updateUser(u.id, { active: !u.active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? res.user : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Update failed'));
    }
  };

  const changeRole = async (u, role) => {
    try {
      const res = await updateUser(u.id, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? res.user : x)));
      toast.success('Role updated');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Update failed'));
    }
  };

  const resetPassword = async () => {
    if (!editing || editPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await updateUser(editing.id, { password: editPw });
      toast.success('Password reset');
      setEditing(null); setEditPw('');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Update failed'));
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success('User deleted');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, 'Delete failed'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-management">
      <AdminHeader
        active="users"
        actions={(
          <button onClick={() => setShowCreate(true)} data-testid="add-user-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87]">
            <UserPlus className="h-4 w-4" /> Add user
          </button>
        )}
      />

      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">User management</h1>
          <p className="text-xs text-slate-500">Admins who can access the marketing CRM · roles: Admin, Editor, Viewer</p>
        </div>

        {locked.length > 0 && (
          <div className="mb-5 overflow-hidden rounded-lg border border-rose-200 bg-rose-50/60" data-testid="locked-accounts">
            <div className="flex items-center gap-2 border-b border-rose-200 bg-rose-100/70 px-5 py-3">
              <LockKeyhole className="h-4 w-4 text-rose-600" />
              <span className="text-sm font-semibold text-rose-800">Locked accounts</span>
              <span className="text-xs text-rose-600">· 3 failed sign-ins locks an account for 24 hours</span>
            </div>
            {locked.map((row) => (
              <div key={row.user_id} data-testid={`locked-row-${row.email}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-rose-100 px-5 py-3 last:border-b-0">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900">{row.name || row.email}</div>
                  <div className="truncate text-xs text-slate-500">{row.email} · {row.attempts} failed attempts · unlocks {fmtDT(row.locked_until)}</div>
                </div>
                <button onClick={() => onUnlock(row)} data-testid={`unlock-${row.email}`} className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#0035A8] px-3 text-xs font-semibold text-white hover:bg-[#002B87]">
                  <Unlock className="h-3.5 w-3.5" /> Unlock now
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-4">User</div>
            <div className="col-span-3">Role</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {loading && <div className="flex items-center justify-center gap-2 py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}

          {!loading && users.map((u) => {
            const rm = roleMeta(u.role);
            return (
              <div key={u.id} data-testid={`user-row-${u.email}`} className="grid grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3.5 md:grid-cols-12 md:items-center md:gap-3">
                <div className="col-span-4 flex min-w-0 items-center gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${rm.cls}`}><rm.icon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{u.name}{u.id === me?.id && <span className="ml-1 text-xs font-normal text-slate-400">(you)</span>}</div>
                    <div className="truncate text-xs text-slate-500">{u.email}</div>
                  </div>
                </div>
                <div className="col-span-3">
                  <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === me?.id} data-testid={`user-role-${u.email}`} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none focus:border-[#0035A8] disabled:opacity-50">
                    {ROLES.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                  </select>
                </div>
                <div className="col-span-2">
                  <button onClick={() => toggleActive(u)} disabled={u.id === me?.id} data-testid={`user-toggle-${u.email}`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium disabled:opacity-50 ${u.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${u.active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                    {u.active ? 'Active' : 'Inactive'}
                  </button>
                </div>
                <div className="col-span-2 text-xs text-slate-500">{fmt(u.created_at)}</div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button onClick={() => { setEditing(u); setEditPw(''); }} className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">Reset PW</button>
                  {u.id !== me?.id && (
                    <button onClick={() => onDelete(u)} data-testid={`user-delete-${u.email}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            );
          })}

          {!loading && users.length === 0 && <div className="py-12 text-center text-sm text-slate-400">No users yet.</div>}
        </div>
      </div>

      {/* Create dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowCreate(false)} />
          <div className="relative w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">Add user</h2>
              <button onClick={() => setShowCreate(false)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={onCreate} className="space-y-3" data-testid="create-user-form">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="create-user-name" className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="create-user-email" className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Temporary password (min 8 chars)</label>
                <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} data-testid="create-user-password" className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="create-user-role" className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]">
                  {ROLES.map((r) => (<option key={r.value} value={r.value}>{r.label} — {r.desc}</option>))}
                </select>
              </div>
              <button type="submit" disabled={saving} data-testid="create-user-submit" className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create user'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reset password dialog */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog">
          <div className="absolute inset-0 bg-black/30" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">Reset password</h2>
              <button onClick={() => setEditing(null)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-3 text-sm text-slate-600">Set a new password for <span className="font-medium">{editing.email}</span>.</p>
            <input type="text" value={editPw} onChange={(e) => setEditPw(e.target.value)} minLength={8} placeholder="New password (min 8 chars)" data-testid="reset-pw-input" className="mb-3 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" />
            <button onClick={resetPassword} data-testid="reset-pw-submit" className="inline-flex h-10 w-full items-center justify-center rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87]">Save password</button>
          </div>
        </div>
      )}
    </div>
  );
}
